import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { getLoggedInServiceNo } from "../../utils/masterDataUtils";
import IsoDateInput from "../common/Isodateinput";
import toIsoDate from "../../utils/dateFormat";
import SearchableSelect from "../common/SearchableSelect";

const calculationMethods = [
  { value: "F", label: "Fixed" },
  { value: "P", label: "Daily-Based" },
];

const INITIAL_FORM = {
  batchNo: "",
  name: "",
  periodId: "",
  componentId: "",
  calculationMethod: "F",
  amount: "",
  percentage: "",
  effectiveDate: toIsoDate(new Date()),
  description: "",
  status: "I",
  approvedBy: getLoggedInServiceNo(),
  isActive: true,
  remarks: "",
};

const inputClass = `
  w-full h-14
  rounded-md
  border border-surface-200
  bg-white
  px-3.5
  text-[16px] text-surface-800
  outline-none
  transition-colors
  focus:border-primary-500
  focus:ring-1 focus:ring-primary-500
`;

const labelClass = `
  absolute
  left-3
  top-0
  -translate-y-1/2
  bg-white
  px-1.5
  text-[13px]
  text-surface-500
  pointer-events-none
  z-10
`;

const errorClass = `!border-red-400 focus:!border-red-500 focus:!ring-red-500`;

const maskCurrencyInput = (rawValue) => {
  const digits = String(rawValue ?? "").replace(/\D/g, "");

  if (!digits) return "";

  const padded = digits.padStart(3, "0");
  const cents = padded.slice(-2);

  const wholeDigits = padded
    .slice(0, -2)
    .replace(/^0+(?=\d)/, "");

  const whole = Number(wholeDigits || "0").toLocaleString("en-US");

  return `${whole}.${cents}`;
};

const parseCurrency = (value) => {
  if (value === "" || value === null || value === undefined) {
    return 0;
  }

  const n = Number(String(value).replace(/,/g, ""));

  return Number.isNaN(n) ? 0 : n;
};

function Field({ label, children, error = "" }) {
  return (
    <div className="relative">
      <label className={labelClass}>
        {label}
      </label>

      {children}

      {error && (
        <p className="mt-1.5 flex items-start gap-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

function TextInput({
  label,
  value,
  onChange,
  type = "text",
  disabled = false,
  error = "",
}) {
  return (
    <Field label={label} error={error}>
      <input
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={`
          ${inputClass}
          ${disabled ? "cursor-not-allowed bg-surface-50 text-surface-400" : ""}
          ${error ? errorClass : ""}
        `}
      />
    </Field>
  );
}

function CurrencyInput({
  label,
  value,
  onChange,
  disabled = false,
  error = "",
}) {
  return (
    <Field label={label} error={error}>
      <input
        type="text"
        inputMode="decimal"
        value={value ?? ""}
        disabled={disabled}
        onChange={(event) =>
          onChange(maskCurrencyInput(event.target.value))
        }
        className={`
          ${inputClass}          
          ${disabled ? "cursor-not-allowed bg-surface-50 text-surface-400" : ""}
          ${error ? errorClass : ""}
        `}
      />
    </Field>
  );
}

function DateInput({ label, value, onChange, error = "" }) {
  return (
    <Field label={label} error={error}>
      <IsoDateInput
        value={value}
        onChange={onChange}
        className={`${inputClass} appearance-none ${
          error ? errorClass : ""
        }`}
      />
    </Field>
  );
}

function SelectInput({
  label,
  value,
  onChange,
  children,
  error = "",
}) {
  return (
    <Field label={label} error={error}>
      <select
        value={value}
        onChange={onChange}
        className={`${inputClass} appearance-auto cursor-pointer ${
          error ? errorClass : ""
        }`}
      >
        {children}
      </select>
    </Field>
  );
}

function TextAreaInput({
  label,
  value,
  onChange,
  rows = 3,
  error = "",
}) {
  return (
    <Field label={label} error={error}>
      <textarea
        value={value}
        onChange={onChange}
        rows={rows}
        className={`
          w-full
          rounded-md
          border border-surface-200
          bg-white
          px-3.5
          py-3
          text-[16px]
          text-surface-800
          outline-none
          transition-colors
          focus:border-primary-500
          focus:ring-1
          focus:ring-primary-500
          ${error ? errorClass : ""}
        `}
      />
    </Field>
  );
}

const AddPayrollBatchDialog = ({
  open,
  onClose,
  onSave,
  periods = [],
  components = [],
  suggestedBatchNo,
}) => {
  const [form, setForm] = useState({
    ...INITIAL_FORM,
    batchNo: suggestedBatchNo || "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;

    setForm({
      ...INITIAL_FORM,
      batchNo: suggestedBatchNo || "",
    });
    setErrors({});
  }, [open, suggestedBatchNo]);

  const handleChange = (key, value) => {
    setForm((prev) => {
      if (key === "calculationMethod") {
        return {
          ...prev,
          calculationMethod: value,
          amount: value === "F" ? prev.amount : "",
          percentage: value === "P" ? prev.percentage : ""
        }
      }

      return {
        ...prev,
        [key]: value,
      }
    })

    setErrors((prev) => ({
      ...prev,
      [key]: "",
    }))
  };

  const validate = () => {
    const newErrors = {};

    if (!String(form.batchNo || "").trim()) {
      newErrors.batchNo = "Batch no is required.";
    }

    if (!form.name.trim()) {
      newErrors.name = "Batch name is required.";
    }

    if (!form.periodId) {
      newErrors.periodId = "Payroll period is required.";
    }

    if (!form.componentId) {
      newErrors.componentId = "Payroll component is required.";
    }

    if (!form.effectiveDate) {
      newErrors.effectiveDate = "Effective date is required.";
    }

    if (form.calculationMethod === "F") {
      if (parseCurrency(form.amount) < 0) {
        newErrors.amount = "Amount cannot be negative.";
      }
    }

    if (form.calculationMethod === "P") {
      if (form.percentage <= 0) {
        newErrors.percentage = "Percentage cannot be 0 or negative.";
      } else if (form.percentage > 100) {
        newErrors.percentage = "Percentage cannot be above 100."
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle className="!px-6 !pt-5 !pb-3">
        <span className="text-[21px] font-medium text-surface-900">
          Add Payroll Batch
        </span>
      </DialogTitle>

      <DialogContent className="!px-6 !py-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">

          {/* Batch No */}
          {/* Pre-filled with the next free number; can be changed. */}
          <TextInput
            label="Batch No"
            value={form.batchNo}
            error={errors.batchNo}
            onChange={(e) =>
              handleChange(
                "batchNo",
                e.target.value
              )
            }
          />

          {/* Batch Name */}
          <TextInput
            label="Batch Name"
            value={form.name}
            error={errors.name}
            onChange={(e) =>
              handleChange(
                "name",
                e.target.value
              )
            }
          />

          {/* Description */}
          <div className="sm:col-span-2">
            <TextAreaInput
              label="Description"
              value={form.description}
              error={errors.description}
              onChange={(e) =>
                handleChange("description", e.target.value)
              }
              rows={1}
            />
          </div>

          {/* OLD native select (kept for reference):
          Payroll Period
          <SelectInput
            label="Payroll Period"
            value={form.periodId}
            error={errors.periodId}
            onChange={(e) =>
              handleChange(
                "periodId",
                e.target.value
              )
            }
          >
            <option value="">
              Select Payroll Period
            </option>

            {periods
              .filter((period) => period.status === "Active")
              .map((period) => (
                <option
                  key={period.id}
                  value={period.id}
                >
                  {period.name}
                </option>
              ))
            }
          </SelectInput>
          */}
          <Field label="Payroll Period" error={errors.periodId}>
            <SearchableSelect
              value={form.periodId}
              onChange={(v) => handleChange("periodId", v)}
              options={periods
                .filter((period) => period.status === "Active")
                .map((period) => ({ value: period.id, label: period.name ?? "" }))}
              emptyOption={{ value: "", label: "Select Payroll Period" }}
              placeholder="Select Payroll Period"
              searchPlaceholder="Search payroll period..."
              className={`${inputClass} cursor-pointer ${errors.periodId ? errorClass : ""}`}
            />
          </Field>

          {/* OLD native select (kept for reference):
          Payroll Component
          <SelectInput
            label="Payroll Component"
            value={form.componentId}
            error={errors.componentId}
            onChange={(e) =>
              handleChange(
                "componentId",
                e.target.value
              )
            }
          >
            <option value="">
              Select Payroll Component
            </option>

            {components
              .filter((component) => !component.basic)
              .map((component) => (
                <option
                  key={component.id}
                  value={component.id}
                >
                  {component.name}
                </option>
            ))}
          </SelectInput>
          */}
          <Field label="Payroll Component" error={errors.componentId}>
            <SearchableSelect
              value={form.componentId}
              onChange={(v) => handleChange("componentId", v)}
              options={components
                .filter((component) => !component.basic)
                .map((component) => ({ value: component.id, label: component.name ?? "" }))}
              emptyOption={{ value: "", label: "Select Payroll Component" }}
              placeholder="Select Payroll Component"
              searchPlaceholder="Search payroll component..."
              className={`${inputClass} cursor-pointer ${errors.componentId ? errorClass : ""}`}
            />
          </Field>

          {/* Calculation Method */}
          <SelectInput
            label="Calculation Method"
            value={form.calculationMethod}
            onChange={(e) =>
              handleChange(
                "calculationMethod",
                e.target.value
              )
            }
          >
            {calculationMethods.map((method) => (
              <option
                key={method.value}
                value={method.value}
              >
                {method.label}
              </option>
            ))}
          </SelectInput>

          {/* Amount */}
          {form.calculationMethod === "F" && (
            <CurrencyInput
              label="Amount"
              value={form.amount}
              error={errors.amount}
              onChange={(value) =>
                handleChange("amount", value)
              }
            />
          )}

          {/* Percentage */}
          {form.calculationMethod === "P" && (
            <TextInput
              label="Percentage"
              type="number"
              value={form.percentage}
              error={errors.percentage}
              onChange={(e) =>
                handleChange(
                  "percentage",
                  e.target.value
                )
              }
            />
          )}

          {/* Effective Date */}
          <DateInput
            label="Effective Date"
            value={form.effectiveDate}
            error={errors.effectiveDate}
            onChange={(value) =>
              handleChange(
                "effectiveDate", value
                // e.target.value
              )
            }
          />

          {/* Remarks */}
          <TextInput
            label="Remarks"
            value={form.remarks}
            error={errors.remarks}
            onChange={(e) =>
              handleChange(
                "remarks",
                e.target.value
              )
            }
          />
        </div>
      </DialogContent>

      <DialogActions className="!px-6 !py-3">
        <Button
          onClick={onClose}
          className="!text-primary-600"
        >
          CANCEL
        </Button>

        <Button
          variant="contained"
          onClick={() => {
            if (validate()) {
              onSave({
                ...form,
                amount:
                  form.calculationMethod === "F"
                    ? parseCurrency(form.amount)
                    : null,
                percentage:
                  form.calculationMethod === "P"
                    ? Number(form.percentage || 0)
                    : null,
              });
            }
          }}
          className="!bg-primary-600 hover:!bg-primary-700"
        >
          SAVE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPayrollBatchDialog;